import { useEffect, useState } from 'react';
import { resolveChatDashboardAccess, type ChatDashboardAccessState } from './chatDashboardAccess';
import oidcAuth from '../../services/auth/oidc-client';
import { localDevAuthBypass } from '../../services/auth/localDevAuthBypass';

export const useChatDashboardAccess = (): ChatDashboardAccessState => {
    const [state, setState] = useState<ChatDashboardAccessState>('checking');
    const bypass = localDevAuthBypass();

    useEffect(() => {
        if (bypass) return;
        let active = true;
        let generation = 0;

        // `sticky` distinguishes a renewal re-check (must not flash
        // 'checking' while already allowed, or it would unmount the guarded
        // page) from this effect's own initial run on mount (must always
        // fail closed from 'checking'). Kept structurally identical to
        // useAiObservabilityAccess so the two hooks do not drift.
        const refresh = (sticky: boolean) => {
            const requestGeneration = ++generation;
            setState((prev) => (sticky && prev === 'allowed' ? prev : 'checking'));
            void resolveChatDashboardAccess()
                .then((allowed) => {
                    if (active && requestGeneration === generation) {
                        setState(allowed ? 'allowed' : 'denied');
                    }
                })
                .catch(() => {
                    if (active && requestGeneration === generation) {
                        setState('denied');
                    }
                });
        };

        refresh(false);
        const unsubscribeLoaded = oidcAuth.onUserLoaded(() => refresh(true));
        const unsubscribeInvalidated = oidcAuth.onSessionInvalidated(() => {
            generation += 1;
            if (active) setState('denied');
        });

        return () => {
            active = false;
            generation += 1;
            unsubscribeLoaded();
            unsubscribeInvalidated();
        };
    }, [bypass]);

    return bypass ? 'allowed' : state;
};
